<template>
  <div v-if="windowWidth > 1024" class="flex flex-col h-[99rem] px-[6.375rem] relative">
    <div
      class="sticky h-[calc(33rem-60px)] flex top-[60px] left-0 pr-[calc(6.375rem/2)]"
      style="width: 50%;"
    >
      <transition :name="`${slideDirection}-slide-fade`" mode="out-in">
        <div
          :key="currentDescription.key"
          class="flex flex-col"
        >
          <h3>
            {{ currentDescription.title }}
          </h3>
          <p class="!text-[1rem] !leading-[1.5rem]">
            {{ currentDescription.description }}
          </p>
        </div>
      </transition>
    </div>
    <div
      class="flex flex-col absolute right-0 pl-[calc(6.375rem/2)]"
      style="width: 50%;"
    >
      <span
        v-for="({id, image, imageAlt}, index) in flattenContainerImgsMapping"
        :key="index"
        :id="id"
        class="h-[33rem] w-[25.5rem] flex items-start justify-center"
      >
        <img
          :src="image"
          :alt="imageAlt"
        />
      </span>
    </div>
  </div>
  <div v-else class="flex flex-col gap-6">
    <div
      v-for="({containerImage, description}, index) in mixedFlattenContainerImgsAndDescriptions"
      :key="index"
    >
      <div class="flex flex-col">
        <div class="flex flex-col">
          <h3>
            {{ description.title }}
          </h3>
          <p class="!text-[1rem] !leading-[1.5rem]">
            {{ description.description }}
          </p>
        </div>
        <span class="w-full flex items-center justify-center">
          <img
            :src="containerImage.image"
            :alt="containerImage.imageAlt"
          />
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RentersGuideVerticalCarousel',
  props: {
    tenFtContainerImageUrl: {
      type: String,
      default: ''
    },
    twentyFtContainerImageUrl: {
      type: String,
      default: ''
    },
    fortyFtContainerImageUrl: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      currentSlide: 1,
      slideDirection: 'up',
      descriptions: [
        { key: 'first-description',
          imagePosition: 1,
          title: 'Choose the Right Container',
          description: 'First, take a good look at the location and decide which container fits the space properly. Shipping containers come in a variety of sizes and door configurations. Depending on the location, a smaller container may be the right fit to accommodate doors easily opening and safe delivery.'
        },
        { key: 'second-description',
          imagePosition: 2,
          title: 'Measure the Delivery Space',
          description: 'Don\'t forget about the delivery trailer and the space required for the truck to maneuver and unload the container. As a general rule, twice the length of the container plus 10 feet in a straight line is needed for a successful delivery. Adequate width is also required but the amount of length is typically the most common issue with delivery. Too often, people wrongly assume 40 ft of space can accommodate a 40ft container. A roll off is the most effective way to deliver shipping containers and ample room in a straight line is essential. Take the time to confirm shipping container sizes and shipping container dimensions, then measure the delivery space to prevent headaches on delivery day.'
        },
        { key: 'third-description',
          imagePosition: 3,
          title: 'Prepare Location',
          description: 'Is the land level? If not, the doors may not open and close easily. Wood blocks under the container is a good solution if you plan to place a container on uneven land. Is there adequate drainage? To avoid damage, sinking, and leveling issues, keep your Corten Steel container out of standing water. In case of a storm, drainage is important. Aside from placing the container on gravel, wood blocks under the container serve as a good solution for this issue as well. Also, please report with pictures of any possible problems with the route taken by the delivery trucks. This includes possible snags such as trees, power lines or other obstructions. Keep in mind, trucks take wide turns, and adequate height clearance (14\') and width clearance (12\') are required.'
        }
      ],
      containerImgsMapping: {
        1: {
          id: 'el-10ft-container',
          image: this.tenFtContainerImageUrl || '../../../images/renters-guides/10ft container.svg',
          imageAlt: '10ft container'
        },
        2: {
          id: 'el-20ft-container',
          image: this.twentyFtContainerImageUrl || '../../../images/renters-guides/20ft container.svg',
          imageAlt: '20ft container'
        },
        3: {
          id: 'el-40ft-container',
          image: this.fortyFtContainerImageUrl || '../../../images/renters-guides/40ft container.svg',
          imageAlt: '40ft container'
        }
      },
      lastScrollTop: 0
    }
  },
  computed: {
    flattenContainerImgsMapping() {
      return Object.values(this.containerImgsMapping)
    },
    currentDescription() {
      return this.descriptions.find(({imagePosition}) => imagePosition === this.currentSlide) || this.descriptions[0]
    },
    mixedFlattenContainerImgsAndDescriptions() {
      return this.flattenContainerImgsMapping.map((containerImage, index) => {
        return {
          description: this.descriptions[index],
          containerImage
        }
      })
    }
  },
  mounted() {
    document.addEventListener('scroll', () => this.handleScroll(document))
    window.addEventListener('resize', () => this.updateWindowWidth(window.innerWidth))
  },
  beforeDestroy() {
    document.removeEventListener('scroll', () => this.handleScroll(document))
    window.removeEventListener('resize', () => this.updateWindowWidth(window.innerWidth))
  },
  methods: {
    updateWindowWidth(width) {
      this.windowWidth = width
    },
    handleScroll(el) {
      if (this.windowWidth <= 1024) return

      const st = window.scrollY || el.scrollTop || el.scrollY || 0
      if (st < this.lastScrollTop) {
        this.slideDirection = 'up'
        if (this.currentSlide > 1) {
          const nextSlide = this.currentSlide - 1
          const nextEl = document.getElementById(this.containerImgsMapping[nextSlide]?.id)
          if (nextEl && nextEl.getBoundingClientRect().bottom > 16.5*16 + 60) {
            this.currentSlide = nextSlide
          }
        }
      } else if (st > this.lastScrollTop) {
        
        this.slideDirection = 'down'
        if (this.currentSlide < 3) {
          const nextSlide = this.currentSlide + 1
          const nextEl = document.getElementById(this.containerImgsMapping[nextSlide]?.id)
          if (nextEl && nextEl.getBoundingClientRect().top < 16.5*16 + 60) {
            this.currentSlide = nextSlide
          }
        }
      }

      this.lastScrollTop = st <= 0 ? 0 : st
    }
  }
}
</script>

<style lang="scss">
.up-slide-fade-enter-active {
  transition: all .1s ease;
}
.up-slide-fade-leave-active {
  transition: all .2s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}
.up-slide-fade-enter {
  transform: translateY(-2rem);
  opacity: 1;
}
.up-slide-fade-leave-to {
  transform: translateY(2rem);
  opacity: 0;
}

.down-slide-fade-enter-active {
  transition: all .1s ease;
}
.down-slide-fade-leave-active {
  transition: all .2s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}
.down-slide-fade-enter {
  transform: translateY(2rem);
  opacity: 1;
}
.down-slide-fade-leave-to {
  transform: translateY(-2rem);
  opacity: 0;
}
</style>